import React, {createContext, useState, useEffect, useCallback, useRef} from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services/authService";
import NotificationService from "../../services/NotificationService";
import {estimateService} from "../../services/EstimateService";
import {publicCustomerService} from "../../services/publicCustomerService";
import notificationService from "../../services/NotificationService";
import authClientStore from "./AuthClientStore";
import AuthClientStore from "./AuthClientStore";
import Time from "react-datepicker/dist/time";
import {registerRefreshToken} from "../../services/common.services";
import eventEmitter from "../../shared/eventEmitter";
import {EventEmitterTypes} from "../../shared/eventEmitterEnum";

interface AuthContextType {
    isAuthenticated: boolean;
    isAdmin: boolean;
    isSuperAdmin: boolean;
    webIdUniqueToken: string | null;
    accessToken: string | null;
    login: (username: string, password: string) => Promise<boolean>;
    customerLoginUsingWebId: (webId: string) => Promise<boolean>;
    logout: () => void;
    getInfo(): Promise<any>;
    refreshAccessToken: any;
}

export const AuthenticationHandler  = createContext<AuthContextType | null>(null);

// buffer to refresh token before expiration
const REFRESH_BUFFER_MS = 90 * 1000;

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
    const navigate = useNavigate();
    const refreshTimeoutRef = useRef<number|null>(null);
    const clearRefreshTimeout = () => {
        if (refreshTimeoutRef.current != null) {
            clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = null;
        }
    };

    const scheduleTokenRefresh = (expiresAt: number) => {
        clearRefreshTimeout();
        const msUntilRefresh = Math.max(0, expiresAt - Date.now() - REFRESH_BUFFER_MS);
        console.log(`scheduling refresh in ${(msUntilRefresh/1000).toFixed(1)}s`);
        refreshTimeoutRef.current = window.setTimeout(refreshAccessToken, msUntilRefresh);
    };

    useEffect(() => {
        const accessToken = authClientStore.getAccessToken();
        const refreshToken = authClientStore.getRefreshToken();
        const expiresAt = authClientStore.getExpiresAt();
        if (expiresAt && accessToken && refreshToken) {
            scheduleTokenRefresh(Number(expiresAt));
        }
        return clearRefreshTimeout;
    }, []);

    function handleAccessExpirationError(error): void {
        console.log(error)
        logout();
        notificationService.error('Access Token has expired. Please log in again');
    }
    
    async function getInfo(): Promise<any> {
        try {
            const userInfo = await authService.getInfo(handleAccessExpirationError);
            const estimateCount = await estimateService.getEstimateTotalForSidebar();
            setIsSuperAdmin(!!userInfo.superAdmin)
            setIsAdmin(true)
            userInfo.incompleteEstimate = estimateCount
            return userInfo;
        } catch (error: any) {
            const status = error.response?.status || error.status;
            if (status === 401) {
                NotificationService.error("Incorrect Username / Password");
            } else {
                NotificationService.handleError(error);
            }
            return null;
        }
    }
    async function login(username: string, password: string): Promise<boolean> {
        try {
            const res = await authService.login(username, password);
            const expiresAt = Date.now() + res.expiresIn * 1000;
            AuthClientStore.setAccessToken(res.accessToken);
            AuthClientStore.setRefreshToken(res.refreshToken);
            AuthClientStore.setExpiresAt(expiresAt.toString());
            scheduleTokenRefresh(expiresAt);
            setIsAdmin(true);
            return true;
        } catch (error: any) {
            const status = error.response?.status || error.status;
            if (status === 401) {
                NotificationService.error("Incorrect Username / Password");
            } else {
                NotificationService.handleError(error);
            }
            return false;
        }
    }
    async function customerLoginUsingWebId(webId: string): Promise<boolean> {
        try {
            const res = await publicCustomerService.getPublicCustomerUniqueId(webId);
            if(!res) return false
            AuthClientStore.setWebUniqueId(res.uniqueId)
            setIsAdmin(false);
            return true;
        } catch (error: any) {
            const status = error.response?.status || error.status;
            if (status === 404) {
                NotificationService.error("User with that WebId not found");
            } else {
                NotificationService.handleError(error);
            }
            return false;
        }
    }

    const logout = useCallback(() => {
        clearRefreshTimeout();
        const adminLogin = AuthClientStore.getAccessToken();
        AuthClientStore.removeAccessToken();
        AuthClientStore.removeRefreshToken();
        authService.logout();
        navigate(adminLogin ? "/admin-login" : "/login");
    }, [navigate, isAdmin]);

    const refreshAccessToken = useCallback(async () => {
        const currentRefreshToken = AuthClientStore.getRefreshToken();
        if (!currentRefreshToken) {
            logout();
            return;
        }
        try {
            // if token is expired. ignore this refresh. it will refresh on its own
            // this is only used on a user reopening a closed tab after the token has expired
            // otherwise leads to a 409 error
            const now = Date.now();
            if(Number(now > Number(AuthClientStore.getExpiresAt()))) {
                eventEmitter.emit(EventEmitterTypes.REFRESH_INFO);
                scheduleTokenRefresh(now + 130 * 1000)
                return;
            }
            const response = await authService.refreshAccess(currentRefreshToken);
            const { accessToken, refreshToken, expiresIn } = response;
            const expiresAt = now + expiresIn * 1000;
            AuthClientStore.setAccessToken(accessToken);
            AuthClientStore.setRefreshToken(refreshToken);
            AuthClientStore.setExpiresAt(expiresAt.toString());
            scheduleTokenRefresh(expiresAt);
        } catch (error) {
            //if current token does not match what is in storage, ignore error
            // this means that a different refresh succeeded at the same time, this is caused by 2+ tabs being open
            console.error("Refresh token failed", error);
            if(currentRefreshToken === AuthClientStore.getRefreshToken())
                logout();
        }
    }, [logout, scheduleTokenRefresh]);




    const contextValue: AuthContextType = {
        isAuthenticated: !!AuthClientStore.getAccessToken() || !!AuthClientStore.getWebUniqueId(),
        isAdmin: !!AuthClientStore.getAccessToken(),
        isSuperAdmin: isSuperAdmin,
        customerLoginUsingWebId,
        accessToken: AuthClientStore.getAccessToken(),
        webIdUniqueToken: AuthClientStore.getWebUniqueId(),
        login,
        logout,
        getInfo,
        refreshAccessToken
    };

    useEffect(() => {
        registerRefreshToken(contextValue.refreshAccessToken);
    }, [contextValue.refreshAccessToken]);
    

    return <AuthenticationHandler.Provider value={contextValue}>{children}</AuthenticationHandler.Provider>;
};

export default AuthProvider;
